import { useCallback, useContext } from 'react'
import { Radio, RadioGroup } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import Text from 'src/components/ui/Text'
import {
  executeSetPreferences,
  updateGetMyPreferences,
} from 'src/services/apollo/preference'
import { track, trackerSetUserProperties } from 'src/utils/tracker'
import { getPromptStyleOptionData } from '../helper'
import { NewUserOnboardingContext } from '../NewUserOnboardingContext'
import { useNewUserOnboardingFormContext } from '../newUserOnboardingForm'
import {
  NewUserOnboardingFormStep,
  NewUserOnboardingSkipButton,
} from '../NewUserOnboardingFormStep'

function PromptStyleSelector(props: {
  onChange: (value: string) => void
  value?: string
  disabled?: boolean
}) {
  const { t, i18n } = useTranslation(['common', 'user'])
  return (
    <RadioGroup
      className="flex flex-col gap-4 my-6"
      onChange={props.onChange}
      aria-label="prompt-style"
      isDisabled={props.disabled}
    >
      {getPromptStyleOptionData().map(
        ({ name: promptStyle, labelI18nKey, exampleI18nKey }) => {
          return (
            <Radio
              key={promptStyle}
              className={twMerge(
                'flex-1 rounded-xl ring-2 transition',
                'flex flex-col p-4 py-8 justify-center gap-2 hover:bg-theme-primary/5 focus-visible:bg-theme-primary/5',
                promptStyle === props.value
                  ? 'ring-theme-primary'
                  : 'ring-neutral-200 dark:ring-neutral-600 hover:ring-theme-primary/50 hover:dark:ring-theme-primary/60 focus-visible:ring-theme-primary/50 focus-visible:dark:ring-theme-primary/60',
              )}
              value={promptStyle}
            >
              <p className="font-bold text-lg">{t(labelI18nKey)}</p>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3">
                <Text
                  i18n={i18n}
                  i18nKey={exampleI18nKey}
                  components={{ br: <br />, b: <b className="text-primary" /> }}
                />
              </p>
            </Radio>
          )
        },
      )}
    </RadioGroup>
  )
}

export function NewUserPromptStyleStep() {
  const { t } = useTranslation(['common', 'user'])
  const form = useNewUserOnboardingFormContext()
  const { promptStyle } = form.values
  const submitPromptStyle = useCallback(async () => {
    const preferences = { promptStyle: form.getValues().promptStyle }
    await executeSetPreferences({ value: preferences })
    updateGetMyPreferences(preferences)
    track('started_choice', {
      promptStyle: promptStyle,
    })
    trackerSetUserProperties(preferences)
  }, [form.getValues])
  const { actionBusy } = useContext(NewUserOnboardingContext)
  return (
    <NewUserOnboardingFormStep
      disableNext={!promptStyle}
      action={submitPromptStyle}
    >
      <h2 className="text-xl font-bold mb-4">
        <NewUserOnboardingSkipButton />
        {t('user:onboarding.prompt-style.title')}
      </h2>
      <PromptStyleSelector
        disabled={actionBusy}
        {...form.getInputProps('promptStyle')}
      />
      <p className="text-xs text-neutral-600 dark:text-neutral-400 italic text-center">
        {t('user:onboarding.prompt-style.recommend')}
      </p>
    </NewUserOnboardingFormStep>
  )
}
